import * as React from "react";
import { ComponentType } from "react";
import { CheckIcon, ChevronsUpDown, XCircle, XIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Badge } from "@/components/catalyst/badge";
import { Button } from "@/components/catalyst/button";
import { Input } from "@headlessui/react";

export type MultiSelectOption = {
  label: string;
  value: string;
  disabled?: boolean;
  icon?: ComponentType<{ className?: string }>;
};

interface MultiSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * An array of option objects to be displayed in the multi-select component.
   * Each option object has a label, value, and an optional icon.
   */
  options: MultiSelectOption[];

  /**
   * Callback function triggered when the selected values change.
   * Receives an array of the new selected values.
   */
  onValueChange: (value: string[]) => void;

  /** The default selected values when the component mounts. */
  defaultValue?: string[];

  /** Controlled value - when provided, component becomes controlled. */
  value?: string[];

  /**
   * Placeholder text to be displayed when no values are selected.
   * Optional, defaults to "Select options".
   */
  placeholder?: string;

  /**
   * Maximum number of items to display. Extra selected items will be summarized.
   * Optional, defaults to 3.
   */
  maxCount?: number;

  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean;

  /**
   * If true, renders the multi-select component as a child of another component.
   * Optional, defaults to false.
   */
  asChild?: boolean;

  /**
   * Additional class names to apply custom styles to the multi-select component.
   * Optional, can be used to add custom styles.
   */
  className?: string;

  /**
   * Whether to show the "Select All" option.
   * Optional, defaults to true.
   */
  showSelectAll?: boolean;

  /**
   * Icon component to display at the start of the input.
   * Optional.
   */
  LeadingIcon?: ComponentType<{ className?: string }>;

  /**
   * Values that cannot be removed from the selection.
   * These will still show as selected but without the remove button.
   */
  lockedValues?: string[];
}

export function MultiSelect({
  options,
  onValueChange,
  defaultValue = [],
  value,
  placeholder = "Select options",
  maxCount = 3,
  modalPopover = false,
  className,
  showSelectAll = true,
  LeadingIcon,
  disabled = false,
  lockedValues = [],
}: MultiSelectProps & { disabled?: boolean }) {
  const [internalSelectedValues, setInternalSelectedValues] =
    React.useState<string[]>(defaultValue);
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);

  // Use controlled value if provided, otherwise use internal state
  const isControlled = value !== undefined;
  const selectedValues = isControlled ? value : internalSelectedValues;
  const setSelectedValues = (newValue: string[]) => {
    if (!isControlled) {
      setInternalSelectedValues(newValue);
    }
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      setIsPopoverOpen(true);
    } else if (event.key === "Backspace" && !event.currentTarget.value) {
      const newSelectedValues = [...selectedValues];
      newSelectedValues.pop();
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    }
  };

  const toggleOption = (option: string) => {
    // Don't allow removing locked values
    if (lockedValues.includes(option) && selectedValues.includes(option)) {
      return;
    }
    const newSelectedValues = selectedValues.includes(option)
      ? selectedValues.filter((value) => value !== option)
      : [...selectedValues, option];
    setSelectedValues(newSelectedValues);
    onValueChange(newSelectedValues);
  };

  const handleClear = () => {
    // Preserve locked values when clearing
    const newValues = selectedValues.filter((v) => lockedValues.includes(v));
    setSelectedValues(newValues);
    onValueChange(newValues);
  };

  const handleTogglePopover = () => {
    setIsPopoverOpen((prev) => !prev);
  };

  const clearExtraOptions = () => {
    const newSelectedValues = selectedValues.slice(0, maxCount);
    setSelectedValues(newSelectedValues);
    onValueChange(newSelectedValues);
  };

  const toggleAll = () => {
    if (selectedValues.length === options.length) {
      handleClear();
    } else {
      const allValues = options.map((option) => option.value);
      setSelectedValues(allValues);
      onValueChange(allValues);
    }
  };

  return (
    <Popover
      open={isPopoverOpen}
      onOpenChange={setIsPopoverOpen}
      modal={modalPopover}
    >
      <PopoverTrigger asChild disabled={disabled}>
        <Input
          as={Button}
          {...{ plain: true }}
          disabled={disabled}
          onClick={disabled ? undefined : handleTogglePopover}
          className={cn(
            "shadow-xs h-auto min-h-[36px] w-full min-w-0 justify-between border-gray-300 text-sm font-normal [&_svg]:pointer-events-auto",
            disabled && "cursor-not-allowed opacity-50",
            className,
          )}
        >
          {LeadingIcon ? (
            <LeadingIcon
              className="mr-2 h-4 w-4 shrink-0 text-gray-400"
              data-slot="icon"
            />
          ) : null}
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
            {selectedValues.length > 0 ? (
              <>
                {selectedValues.slice(0, maxCount).map((value) => {
                  const option = options.find((o) => o.value === value);
                  const IconComponent = option?.icon;
                  const isLocked = lockedValues.includes(value);
                  return (
                    <Badge key={value}>
                      {IconComponent && (
                        <IconComponent className="mr-2 h-4 w-4" />
                      )}
                      {option?.label}
                      {!disabled && !isLocked ? (
                        <XCircle
                          className="ml-2 h-4 w-4 cursor-pointer hover:text-red-500"
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleOption(value);
                          }}
                        />
                      ) : null}
                    </Badge>
                  );
                })}
                {selectedValues.length > maxCount && (
                  <Badge>
                    {`+ ${selectedValues.length - maxCount} more`}
                    {!disabled ? (
                      <XCircle
                        className="ml-2 h-4 w-4 cursor-pointer hover:text-red-500"
                        onClick={(event) => {
                          event.stopPropagation();
                          clearExtraOptions();
                        }}
                      />
                    ) : null}
                  </Badge>
                )}
              </>
            ) : (
              <span className="truncate text-sm text-gray-400">
                {placeholder}
              </span>
            )}
          </span>
          {selectedValues.length > 0 && !disabled ? (
            <XIcon
              className="h-4 w-4 shrink-0 cursor-pointer opacity-50 hover:text-red-500 hover:opacity-100"
              onClick={(event) => {
                event.stopPropagation();
                handleClear();
              }}
            />
          ) : (
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          )}
        </Input>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        onEscapeKeyDown={() => setIsPopoverOpen(false)}
      >
        <Command>
          <CommandInput
            placeholder="Search..."
            onKeyDown={handleInputKeyDown}
          />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {showSelectAll ? (
                <CommandItem
                  key="all"
                  onSelect={toggleAll}
                  className="cursor-pointer"
                >
                  <div
                    className={cn(
                      "border-primary mr-2 flex h-4 w-4 items-center justify-center rounded-sm border",
                      selectedValues.length === options.length
                        ? "bg-primary text-primary-foreground"
                        : "opacity-50 [&_svg]:invisible",
                    )}
                  >
                    <CheckIcon className="h-4 w-4" />
                  </div>
                  <span>(Select All)</span>
                </CommandItem>
              ) : null}
              {options.map((option) => {
                const isSelected = selectedValues.includes(option.value);
                const isLocked = lockedValues.includes(option.value);
                // Locked items that are selected cannot be toggled off
                const isDisabled = option.disabled || (isLocked && isSelected);
                return (
                  <CommandItem
                    disabled={isDisabled}
                    key={option.value}
                    onSelect={() => toggleOption(option.value)}
                    className={cn("cursor-pointer", isDisabled && "opacity-50")}
                    value={option.label}
                  >
                    <div
                      className={cn(
                        "border-primary mr-2 flex h-4 w-4 items-center justify-center rounded-sm border",
                        isSelected
                          ? "bg-primary text-primary-foreground"
                          : "opacity-50 [&_svg]:invisible",
                      )}
                    >
                      <CheckIcon className="h-4 w-4" />
                    </div>
                    {option.icon && (
                      <option.icon className="mr-2 h-4 w-4 text-gray-500" />
                    )}
                    <span>{option.label}</span>
                    {isLocked && isSelected ? (
                      <span className="ml-auto text-xs text-gray-400">
                        Primary
                      </span>
                    ) : null}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selectedValues.length > 0 ? (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={handleClear}
                    className="cursor-pointer justify-center"
                  >
                    Clear
                  </CommandItem>
                </CommandGroup>
              </>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
